import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { gradeApi } from '../api.js';
import { useAuth } from '../auth/AuthContext';
import ConfirmationModal from './ConfirmationModal';
import './sidebar.scss';

const SideBar = ({ activePage }) => {
  const { user, isLoading, logout } = useAuth();
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [regrades, setRegrades] = useState(false);
  const [tokenUsage, setTokenUsage] = useState({
    credits_used: 0,
    credit_limit: 100
  });
  const [tokenUsageLoading, setTokenUsageLoading] = useState(true);

  useEffect(() => {
    if (user && user.session_token) {
      const getRegrades = async () => {
        try {
          const response = await gradeApi.get({
            path: `/api/has-regrade-requests/`,
            config: { headers: { Authorization: `Bearer ${user.session_token}` } }
          });
          setRegrades(response.data['has_regrade_requests']);
        } catch (error) {
          console.log(error);
        }
      };

      const getTokenUsage = async () => {
        try {
          setTokenUsageLoading(true);
          const response = await gradeApi.get({
            path: `/api/account/token-usage`,
            config: { headers: { Authorization: `Bearer ${user.session_token}` } }
          });
          setTokenUsage(response.data.data);
        } catch (error) {
          console.log('Error fetching token usage:', error);
        } finally {
          setTokenUsageLoading(false);
        }
      };

      getRegrades();
      getTokenUsage();
    }
  }, [user]);

  const isActiveTab = tabName => tabName === activePage;

  const handleLogout = () => setIsLogoutModalOpen(true);
  const confirmLogout = () => {
    logout();
    setIsLogoutModalOpen(false);
  };

  const SidebarItem = ({ to, icon, activeIcon, label, tabName }) => (
    <Link to={to} className={`sidebar-item ${isActiveTab(tabName) ? 'active' : ''}`}>
      <img
        src={isActiveTab(tabName) ? activeIcon : icon}
        alt={label}
        className="sidebar-item-icon"
      />
      <span className="sidebar-item-label">{label}</span>
      {tabName === 'Regrade' && regrades && <div className="notification-dot"></div>}
    </Link>
  );

  const TokenUsageBar = () => {
    const creditsRemaining = Math.max(tokenUsage.credit_limit - tokenUsage.credits_used, 0);
    const percentageRemaining = Math.max((creditsRemaining / tokenUsage.credit_limit) * 100, 0);

    let barColor;
    if (percentageRemaining < 20) {
      barColor = '#ef4444';
    } // red-500
    else if (percentageRemaining < 50) {
      barColor = '#f97316';
    } // orange-500
    else {
      barColor = '#22c55e';
    } // green-500

    return (
      <div className="token-usage-bar">
        <div className="header">
          <div className="title">AI Credits</div>
          <div className="usage">
            {tokenUsageLoading ? 'Loading...' : `${creditsRemaining} / ${tokenUsage.credit_limit}`}
          </div>
        </div>
        <div className="bar-background">
          {!tokenUsageLoading && (
            <div
              className="bar-foreground"
              style={{
                width: `${percentageRemaining}%`,
                backgroundColor: barColor
              }}
            />
          )}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="w-64 h-screen bg-white border-r border-gray-200 flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <>
      <ConfirmationModal
        isOpen={isLogoutModalOpen}
        message="Are you sure you want to log out?"
        onClose={() => setIsLogoutModalOpen(false)}
        onConfirm={confirmLogout}
      />
      <div id="sidebar">
        <div>
          <Link to={user.user.is_teacher ? '/tclasses' : '/sclasses'} className="logo-container">
            <img src="/assets/logo_icon.png" alt="GradeWiz Icon" className="logo-icon" />
            <span className="logo-text">GradeWiz</span>
            <div className="beta-tag">Beta</div>
          </Link>

          <nav className="nav-items">
            <SidebarItem
              to={user.user.is_teacher ? '/tclasses' : '/sclasses'}
              icon="/assets/sidebar/menu-home.png"
              activeIcon="/assets/sidebar/menu-home-white.png"
              label="My Classes"
              tabName="Home"
            />
            {user.user.is_teacher && (
              <SidebarItem
                to="/regrade"
                icon="/assets/sidebar/quote-request.png"
                activeIcon="/assets/sidebar/quote-request-white.png"
                label="Regrade Requests"
                tabName="Regrade"
              />
            )}
            <SidebarItem
              to="/support"
              icon="/assets/sidebar/support.png"
              activeIcon="/assets/sidebar/support-white.png"
              label="Support & Feedback"
              tabName="Support"
            />
          </nav>
        </div>

        <div className="sidebar-footer">
          <TokenUsageBar />
          <div className="profile-section">
            <div className="user-info">
              <img src="/assets/sidebar/Profile.png" alt="Profile" className="profile-icon" />
              <div className="user-details">
                <div className="user-name">
                  {user.user.first_name} {user.user.last_name}
                </div>
                <div className="user-id">ID: {user.user.id}</div>
              </div>
            </div>
            <div className="profile-actions">
              <Link to="/profile" className="action-button">
                <img
                  src="/assets/sidebar/account_settings.png"
                  className="action-icon"
                  alt="Account Settings"
                />
              </Link>
              <button className="action-button" onClick={handleLogout}>
                <img src="/assets/sidebar/logout.png" className="action-icon" alt="Logout" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SideBar;
